import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useI18n } from '../../contexts/I18nContext';
import { useBrand } from '../../../brands/contexts/BrandContext';
import {
  buildSubscriptionPaymentFailUrl,
  buildSubscriptionPaymentSuccessUrlBase,
  getActiveTariffs,
  startSubscriptionPayment,
  type AdminTariff,
  type BillingPeriod,
} from '../../utils/subscriptionApiUtils';
import { formatMoney, protocolLimitLabel, tariffPriceLabel } from './subscriptionFormatters';
import './SubscriptionPages.scss';

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useI18n();
  const { brand } = useBrand();
  const [tariff, setTariff] = useState<AdminTariff | null>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  const tariffId = Number(searchParams.get('tariff_id'));
  const period = (searchParams.get('period') === 'yearly' ? 'yearly' : 'monthly') as BillingPeriod;

  const logoUrl = `${process.env.PUBLIC_URL}/${brand.logos.small}`;

  const origin = useMemo(() => window.location.origin, []);

  useEffect(() => {
    sessionStorage.removeItem('subscriptionPostPaymentRedirect');
    sessionStorage.removeItem('subscriptionPostPaymentSnackbar');
  }, []);

  useEffect(() => {
    if (!tariffId || Number.isNaN(tariffId)) {
      navigate('/subscription/tariffs', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await getActiveTariffs();
        if (cancelled) return;
        const found = list.find((x) => x.id === tariffId);
        if (!found) {
          enqueueSnackbar(t('subscription.tariffNotFound'), { variant: 'error' });
          navigate('/subscription/tariffs', { replace: true });
          return;
        }
        setTariff(found);
      } catch (e) {
        if (!cancelled) {
          enqueueSnackbar(e instanceof Error ? e.message : t('subscription.loadError'), { variant: 'error' });
          navigate('/subscription/tariffs', { replace: true });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tariffId, navigate, enqueueSnackbar, t]);

  const handlePay = async () => {
    if (!tariff) return;
    setPaying(true);
    try {
      const successUrl = buildSubscriptionPaymentSuccessUrlBase(origin);
      const failUrl = buildSubscriptionPaymentFailUrl(origin);
      const { payment_url } = await startSubscriptionPayment({
        tariff_id: tariff.id,
        billing_period: period,
        success_url: successUrl,
        fail_url: failUrl,
      });
      window.location.href = payment_url;
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : t('subscription.paymentStartError'), { variant: 'error' });
      setPaying(false);
    }
  };

  if (loading || !tariff) {
    return (
      <div className="subscription-page">
        <div className="subscription-page__loader">{t('subscription.loading')}</div>
      </div>
    );
  }

  const amount =
    period === 'monthly' ? tariff.price_monthly : tariff.price_yearly_total;

  return (
    <div className="subscription-page">
      <header className="subscription-page__header">
        <button type="button" className="subscription-page__back" onClick={() => navigate(-1)} aria-label="Back">
          ←
        </button>
        <h1 className="subscription-page__title">{t('subscription.paymentTitle')}</h1>
      </header>

      <div className="subscription-checkout-grid">
        <div className="subscription-payment-summary">
          <img src={logoUrl} alt="" className="subscription-payment-summary__logo" />
          <div className="subscription-payment-summary__name">{tariff.name}</div>
          <div className="subscription-payment-summary__price">{tariffPriceLabel(tariff, period)}</div>
          <p className="subscription-payment-summary__feat">{protocolLimitLabel(tariff)}</p>
        </div>

        <div className="subscription-checkout-aside">
          <section className="subscription-section-card">
            <h2 className="subscription-section-card__title">{t('subscription.paymentMethod')}</h2>
            <div className="subscription-payment-method">
              <span className="subscription-payment-method__icon">💳</span>
              <div>
                <div className="subscription-payment-method__name">{t('subscription.cardPayment')}</div>
                <div className="subscription-payment-method__hint">{t('subscription.cardPaymentHint')}</div>
              </div>
            </div>
          </section>

          <button
            type="button"
            className="subscription-btn subscription-btn--primary subscription-btn--block subscription-btn--large"
            disabled={paying}
            onClick={() => void handlePay()}
          >
            {paying ? '…' : `${t('subscription.payButton')} ${formatMoney(amount, tariff.currency)}`}
          </button>

          <p className="subscription-legal">{t('subscription.payLegal')}</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
